//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  getCampaignAutocomplete,
  getCampaignExport,
  getCampaignMedia,
} from '@apis/campaigns/campaignsApi';
import {
  CampaignsMediaSort,
  CampaignType,
  ICampaign,
} from '@apis/campaigns/campaignsApiModel';
import { IAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import { ISearchTypeAheadData } from '@components/molecules/search-bar-with-type-ahead/SearchTypeAhead';
import DateString from '@models/DateString';
import InstagramHashtag from '@models/InstagramHashtag';
import InstagramUsername from '@models/InstagramUsername';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { getFullCampaignDateRange } from '@sampling/campaignUtils';
import {
  CAMPAIGNS_MEDIA_REQUEST_LIMIT,
  DEFAULT_SEARCH_SORT_KEY,
  ICampaignDetailsState,
  ICampaignMedia,
  ICampaignSearchTermData,
} from '@sampling/redux/campaign-details/campaignDetailsModel';
import { checkHasReachedEndOfContent } from '@sampling/redux/campaign-details/campaignDetailsUtils';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

const initialMediaState: ICampaignMedia = {
  media: [],
  searchOffset: 0,
  stats: undefined,
  totalCount: 0,
};

const initialState: ICampaignDetailsState = {
  ...initialMediaState,

  autocompleteResults: {
    hashtags: [],
    labels: [],
    usernames: [],
  },
  dateRange: undefined,
  searchTerms: {
    searchedHashtags: [],
    searchedUsernames: [],
  },
  sort: DEFAULT_SEARCH_SORT_KEY,

  errors: {
    fetchAdditionalCampaignMediaError: undefined,
    fetchCampaignMediaError: undefined,
    fetchCampaignSearchAutocompleteError: undefined,
  },
  flags: {
    hasReachedEndOfCampaignMedia: false,
    isFetchingCampaignMedia: false,
    isFetchingAdditionalCampaignMedia: false,
    isFetchingCampaignSearchAutocompleteResults: false,
  },
};

const { actions, reducer: CampaignDetailsReducer } = createSlice({
  name: SliceNames.CampaignDetails,
  initialState,
  reducers: {
    // Initial Campaign Media
    requestCampaignMedia(state) {
      // Reset Media to initial state
      return {
        ...state,
        ...initialMediaState,
        errors: {
          ...state.errors,
          fetchCampaignMediaError: undefined,
        },
        flags: {
          ...state.flags,
          hasReachedEndOfCampaignMedia: false,
          isFetchingCampaignMedia: true,
        },
      };
    },
    receiveCampaignMediaSucceeded(
      state,
      { payload: campaignMedia }: PayloadAction<ICampaignMedia>,
    ) {
      // Replace existing campaign media with new search results
      return {
        ...state,
        ...campaignMedia,
        flags: {
          ...state.flags,
          hasReachedEndOfCampaignMedia: checkHasReachedEndOfContent(
            campaignMedia.media.length,
            CAMPAIGNS_MEDIA_REQUEST_LIMIT,
          ),
          isFetchingCampaignMedia: false,
        },
      };
    },
    receiveCampaignMediaFailed(
      state,
      { payload: errorMsg }: PayloadAction<string>,
    ) {
      state.flags.hasReachedEndOfCampaignMedia = false;
      state.flags.isFetchingCampaignMedia = false;
      state.errors.fetchCampaignMediaError = errorMsg;
    },

    // Additional Campaign Media
    requestAdditionalCampaignMedia(state) {
      state.errors.fetchAdditionalCampaignMediaError = undefined;
      state.flags.isFetchingAdditionalCampaignMedia = true;
    },
    receiveAdditionalCampaignMediaSucceeded(
      state,
      { payload: campaignMedia }: PayloadAction<ICampaignMedia>,
    ) {
      // Add additional media to existing campaign media and replace other metadata
      state.media = [...state.media, ...campaignMedia.media];
      state.searchOffset = campaignMedia.searchOffset;
      state.stats = campaignMedia.stats;
      state.totalCount = campaignMedia.totalCount;

      state.flags.hasReachedEndOfCampaignMedia = checkHasReachedEndOfContent(
        campaignMedia.media.length,
        CAMPAIGNS_MEDIA_REQUEST_LIMIT,
      );
      state.flags.isFetchingAdditionalCampaignMedia = false;
    },
    receiveAdditionalCampaignMediaFailed(
      state,
      { payload: errorMsg }: PayloadAction<string>,
    ) {
      state.flags.hasReachedEndOfCampaignMedia = true;
      state.flags.isFetchingAdditionalCampaignMedia = false;
      state.errors.fetchAdditionalCampaignMediaError = errorMsg;
    },

    // Campaign Search Autocomplete
    requestCampaignSearchAutocomplete(state) {
      state.errors.fetchCampaignSearchAutocompleteError = undefined;
      state.flags.isFetchingCampaignSearchAutocompleteResults = true;
    },
    receiveCampaignAutoCompleteSucceeded(
      state,
      {
        payload: autocompleteResults,
      }: PayloadAction<ICampaignDetailsState['autocompleteResults']>,
    ) {
      state.autocompleteResults = autocompleteResults;
      state.flags.isFetchingCampaignSearchAutocompleteResults = false;
    },
    receiveCampaignAutoCompleteFailed(
      state,
      { payload: errorMsg }: PayloadAction<string>,
    ) {
      state.flags.isFetchingCampaignSearchAutocompleteResults = false;
      state.errors.fetchCampaignSearchAutocompleteError = errorMsg;
    },

    setCampaignSearchDateRange(
      state,
      { payload: dateRange }: PayloadAction<IAliasedDateRange>,
    ) {
      state.dateRange = dateRange;
    },
    setCampaignSortValue(
      state,
      { payload: sort }: PayloadAction<CampaignsMediaSort>,
    ) {
      state.sort = sort;
    },
    setCampaignSearchTerm(
      state,
      { payload: searchTerm }: PayloadAction<ISearchTypeAheadData>,
    ) {
      if (searchTerm.type === 'hashtag') {
        state.searchTerms.searchedHashtags.push(
          InstagramHashtag(searchTerm.label),
        );
      } else {
        state.searchTerms.searchedUsernames.push(
          InstagramUsername(searchTerm.label),
        );
      }
    },
    removeCampaignSearchTerm(
      state,
      { payload: searchTerm }: PayloadAction<ICampaignSearchTermData>,
    ) {
      if (searchTerm.type === 'hashtag') {
        return {
          ...state,
          searchTerms: {
            ...state.searchTerms,
            searchedHashtags: state.searchTerms.searchedHashtags.filter(
              (tag) => {
                return tag !== searchTerm.term;
              },
            ),
          },
        };
      }

      if (searchTerm.type === 'username') {
        return {
          ...state,
          searchTerms: {
            ...state.searchTerms,
            searchedUsernames: state.searchTerms.searchedUsernames.filter(
              (name) => name !== searchTerm.term,
            ),
          },
        };
      }
    },
    removeAllCampaignSearchTerms(state) {
      state.searchTerms.searchedHashtags = [];
      state.searchTerms.searchedUsernames = [];
    },

    resetCampaignDetails(_state) {
      return initialState;
    },
  },
});

export const {
  removeAllCampaignSearchTerms,
  removeCampaignSearchTerm,
  resetCampaignDetails,
  setCampaignSearchDateRange,
  setCampaignSearchTerm,
  setCampaignSortValue,
} = actions;

export const fetchCampaignMedia = createThunk(
  (campaignType: CampaignType, campaignId: string) =>
    async (dispatch, getState) => {
      const {
        dateRange,
        flags: { isFetchingCampaignMedia },
        searchTerms,
        sort,
      } = getState().campaigns.campaignDetails;

      // Return early if already fetching campaign media
      if (isFetchingCampaignMedia) {
        return;
      }

      // If a dateRange has not been set we will be in an error state
      // the dateRange should be initialized to the full campaign
      // dateRange when the campaign details component is loaded
      if (!isDefined(dateRange)) {
        return;
      }

      dispatch(actions.requestCampaignMedia());

      try {
        const campaignMedia = await getCampaignMedia(
          campaignType,
          campaignId,
          {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            orderBy: sort,
            limit: CAMPAIGNS_MEDIA_REQUEST_LIMIT,
            offset: 0,
          },
          searchTerms,
        );

        dispatch(
          actions.receiveCampaignMediaSucceeded({
            media: campaignMedia.media,
            searchOffset: campaignMedia.offset,
            stats: campaignMedia.stats,
            totalCount: campaignMedia.totalCount,
          }),
        );
      } catch (e) {
        dispatch(
          actions.receiveCampaignMediaFailed(getRequestErrorMessage(e) ?? ''),
        );
      }
    },
);

export const fetchUnfilteredCampaignMedia = createThunk(
  (campaign: ICampaign) => async (dispatch, getState) => {
    const userTimeZoneId = getUserTimeZone(getState());
    const startDateString = DateString.fromTimestamp(
      campaign.start,
      userTimeZoneId,
    );
    const endDateString = DateString.fromTimestamp(
      campaign.end,
      userTimeZoneId,
    );

    dispatch(
      setCampaignSearchDateRange(
        getFullCampaignDateRange({
          start: startDateString,
          end: endDateString,
          userTimeZoneId,
        }),
      ),
    );

    return dispatch(
      fetchCampaignMedia(campaign.campaignType, campaign.campaignId),
    );
  },
);

export const fetchAdditionalCampaignMedia = createThunk(
  (campaignType: CampaignType, campaignId: string) =>
    async (dispatch, getState) => {
      const {
        dateRange,
        flags: {
          hasReachedEndOfCampaignMedia,
          isFetchingCampaignMedia,
          isFetchingAdditionalCampaignMedia,
        },
        searchTerms,
        searchOffset,
        sort,
      } = getState().campaigns.campaignDetails;

      // Return early if already fetching campaign media
      // or if we have reached the end of the campaign media
      if (
        isFetchingCampaignMedia ||
        isFetchingAdditionalCampaignMedia ||
        hasReachedEndOfCampaignMedia
      ) {
        return;
      }

      // If a dateRange has not been set we will be in an error state
      // the dateRange should be initialized to the full campaign
      // dateRange when the campaign details component is loaded
      if (!isDefined(dateRange)) {
        return;
      }

      dispatch(actions.requestAdditionalCampaignMedia());

      try {
        const campaignMedia = await getCampaignMedia(
          campaignType,
          campaignId,
          {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            orderBy: sort,
            limit: CAMPAIGNS_MEDIA_REQUEST_LIMIT,
            offset: searchOffset,
          },
          searchTerms,
        );

        dispatch(
          actions.receiveAdditionalCampaignMediaSucceeded({
            media: campaignMedia.media,
            searchOffset: campaignMedia.offset,
            stats: campaignMedia.stats,
            totalCount: campaignMedia.totalCount,
          }),
        );
      } catch (e) {
        dispatch(
          actions.receiveAdditionalCampaignMediaFailed(
            getRequestErrorMessage(e) ?? '',
          ),
        );
      }
    },
);

export const fetchCampaignSearchAutocomplete = createThunk(
  (campaignType: CampaignType, campaignId: string, searchTerm: string = '') =>
    async (dispatch, getState) => {
      const { dateRange, searchTerms } = getState().campaigns.campaignDetails;

      // If a dateRange has not been set we will be in an error state
      // the dateRange should be initialized to the full campaign
      // dateRange when the campaign details component is loaded
      if (!isDefined(dateRange)) {
        return;
      }

      dispatch(actions.requestCampaignSearchAutocomplete());

      try {
        const autocompleteResults = await getCampaignAutocomplete(
          campaignType,
          campaignId,
          {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            searchTerm,
          },
          searchTerms,
        );

        dispatch(
          actions.receiveCampaignAutoCompleteSucceeded(autocompleteResults),
        );
      } catch (e) {
        dispatch(
          actions.receiveCampaignAutoCompleteFailed(
            getRequestErrorMessage(e) ?? '',
          ),
        );
      }
    },
);

export const fetchCampaignSummaryExport = createThunk(
  (campaignType: CampaignType, campaignId: string) =>
    async (_dispatch, getState) => {
      const { dateRange, searchTerms, sort } =
        getState().campaigns.campaignDetails;

      try {
        // If a dateRange has not been set we will be in an error state
        // the dateRange should be initialized to the full campaign
        // dateRange when the campaign details component is loaded
        if (!isDefined(dateRange)) {
          throw new Error('No dateRange set.');
        }

        const blob = await getCampaignExport(
          campaignType,
          campaignId,
          {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            orderBy: sort,
          },
          {
            searchedHashtags: searchTerms.searchedHashtags,
            searchedUsernames: searchTerms.searchedUsernames,
          },
        );

        return blob;
      } catch (e) {
        console.error(
          `crl8: An error occured while generating Campaign summary export: ${campaignType} ${campaignId}. ${
            getRequestErrorMessage(e) ?? ''
          }`,
        );

        return new Blob();
      }
    },
);

export default CampaignDetailsReducer;
